import React, { FC, useState, useEffect, useContext, useId } from 'react';
import classnames from 'classnames';
import { Svg } from 'src/components/Svg/Svg';
import { ConfigContext } from 'src/components/AppShell/ConfigProvider';

import classes from './YotpoReviews.scss';

type Props = {
    urlKey: string;
    productId: number;
    price: number;
    name: string;
    image: string;
    isBundle?: boolean;
};

export const YotpoReviews: FC<Props> = ({ productId, price, name, image, isBundle = false }) => {
    const { currentCurrency } = useContext(ConfigContext);
    const [isOpenReview, setIsOpenReview] = useState(false);
    const [isOpenQA, setIsOpenQA] = useState(false);
    const [isYotpoEnabled, setIsYotpoEnabled] = useState(true);
    const reviewsId = useId();
    const questionsId = useId();

    useEffect(() => {
        const className = 'yotpo-widget-instance';
        let observer: MutationObserver | null = null;

        const checkAndLog = () => {
            const el = document.getElementsByClassName(className)[0];
            if (el && el.getAttribute('data-yotpo-element-loaded') === 'true') {
                setTimeout(() => {
                    const stars = document.querySelectorAll('.yotpo-sr-bottom-line-summary');

                    if (stars[0]) {
                        setIsYotpoEnabled(true);
                        if (observer) observer.disconnect();
                    } else {
                        setIsYotpoEnabled(false);
                        if (observer) observer.disconnect();
                    }
                }, 1000);
            }
        };

        observer = new MutationObserver(() => {
            checkAndLog();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-yotpo-element-loaded'],
        });

        checkAndLog();

        return () => {
            if (observer) observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const getScore = (percent: string) => {
            switch (percent) {
                case '0%':
                    return 1;
                case '25%':
                    return 2;
                case '50%':
                    return 3;
                case '75%':
                    return 4;
                case '100%':
                    return 5;
                default:
                    return 0;
            }
        };

        const getReviewContent = (score: number, name: string) => {
            const container = document?.createElement('div');
            container.className = 'reviewContent';
            const title = document?.createElement('span');
            title.appendChild(document?.createTextNode(name));
            container.appendChild(title);
            const imageContainer = document?.createElement('div');

            for (let i = 0; i < score; i++) {
                const img = document?.createElement('img');
                img.src = require(`../../components/Svg/svgs/filled-star.svg`);
                img.alt = 'star icon';
                imageContainer.appendChild(img);
            }

            for (let i = 0; i < 5 - score; i++) {
                const img = document?.createElement('img');
                img.src = require(`../../components/Svg/svgs/empty-star.svg`);
                img.alt = 'star icon';
                imageContainer.appendChild(img);
            }
            container.appendChild(imageContainer);

            return container;
        };

        const addYotpoStars = () => {
            const collectionOfReviews = document?.getElementsByClassName(
                'yotpo-progress-bar-score',
            ) as HTMLCollectionOf<HTMLElement>;
            const arrayOfReviews = [...collectionOfReviews];

            if (arrayOfReviews.length > 0) {
                arrayOfReviews?.forEach((review) => {
                    if (review?.parentNode?.parentNode?.parentNode?.children) {
                        const childrenList = [...review?.parentNode?.parentNode?.parentNode?.children];

                        const isAlreadyAddedContent =
                            childrenList?.findIndex((child) => child?.className === 'reviewContent') >= 0;

                        if (getScore(review?.style?.width) > 0 && !isAlreadyAddedContent) {
                            review.parentNode?.parentNode?.parentNode?.appendChild(
                                getReviewContent(
                                    getScore(review?.style?.width),
                                    childrenList?.find(
                                        (child) => child?.className === 'yotpo-custom-questions-range-question-title',
                                    )?.innerHTML || '',
                                ),
                            );
                        }
                    }
                });
            }
        };

        const seeMoreButton = document?.getElementsByClassName('yotpo-custom-questions-see-more-label')[0];

        if (seeMoreButton) {
            seeMoreButton.addEventListener('click', () => {
                addYotpoStars();
            });
        }

        addYotpoStars();
    }, [isOpenReview]);

    useEffect(() => {
        const s1 = document.createElement('script');
        s1.type = 'text/javascript';
        s1.textContent = `(function e(){var e=document.createElement("script");e.type="text/javascript",e.async=true,e.src="//staticw2.yotpo.com/4vZm0N8PJwYOzQV7P7ZpIgtDqr9sCFZoX6l9Hvvw/widget.js";var t=document.getElementsByTagName("script")[0];t.parentNode.insertBefore(e,t)})();`;
        document.head.appendChild(s1);
        const s2 = document.createElement('script');
        s2.textContent =
            "if (typeof Yotpo !== 'undefined' && typeof yotpo !== 'undefined') { new Yotpo.API(yotpo).refreshWidgets(); }";
        document.head.appendChild(s2);
        return () => {
            s1.remove();
            s2.remove();
        };
    }, []);

    return (
        <>
            {isYotpoEnabled && (
                <div
                    className={classnames(classes.reviews, {
                        [classes.isOpen]: isOpenReview,
                        [classes.bundle]: isBundle,
                    })}
                >
                    <h2>
                        <button
                            className={classes.toggle}
                            type="button"
                            onClick={() => setIsOpenReview(!isOpenReview)}
                            aria-expanded={isOpenReview}
                            aria-controls={reviewsId}
                        >
                            Reviews
                            <span className={classes.arrow}>
                                <Svg name={isOpenReview ? 'arrow-up' : 'arrow-down'} />
                            </span>
                        </button>
                    </h2>
                    <div
                        id={reviewsId}
                        inert={!isOpenReview}
                        className="yotpo yotpo-main-widget"
                        data-product-id={productId}
                        data-price={price}
                        data-currency={currentCurrency.code}
                        data-name={name}
                        data-image-url={image}
                    />
                </div>
            )}
            <div
                className={classnames(classes.questions, {
                    [classes.isOpen]: isOpenQA,
                    [classes.bundle]: isBundle,
                })}
            >
                <h2>
                    <button
                        className={classes.toggle}
                        type="button"
                        onClick={() => setIsOpenQA(!isOpenQA)}
                        aria-expanded={isOpenQA}
                        aria-controls={questionsId}
                    >
                        Questions & Answers
                        <span className={classes.arrow}>
                            <Svg name={isOpenQA ? 'arrow-up' : 'arrow-down'} />
                        </span>
                    </button>
                </h2>
                <div
                    id={questionsId}
                    inert={!isOpenQA}
                    className="yotpo yotpo-main-widget"
                    data-product-id={productId}
                    data-name={name}
                    data-image-url={image}
                    data-mode="questions"
                    data-price={price}
                />
            </div>
        </>
    );
};
